import * as Sentry from '@sentry/react'
import { matchRoute } from './app/router/routes'

/**
 * Sentry — error monitoring and tracing for the PWA.
 *
 * ⚠️ IMPORTED FIRST IN `main.tsx`, BEFORE ANYTHING ELSE. The SDK hooks
 * `window.onerror`, `fetch` and `history` when `init` runs; a module that
 * throws while it is being evaluated ahead of this one is never reported.
 *
 * The DSN is public by design — it can send events, not read them — so it
 * lives here rather than in a build variable that a deploy could forget.
 */

/*
 * 🔴 SEVERAL ADDRESSES IN THIS APP CARRY A CREDENTIAL IN THE PATH: the invite,
 * school sign-up, password-reset and quote links are all `#/<prefix>/<token>`,
 * and a quote token is also in the API path it calls. The token IS the
 * authentication, so it must not ride out to a third party in an event URL, a
 * breadcrumb or a span name. Everything sent goes through `scrub` first.
 */
const TOKEN_IN_PATH = /(#\/(?:invite|signup|reset|quote)\/|\/public\/quotes\/)[^/?#&\s"'\\]+/g

function scrubString(value: string): string {
  return value.replace(TOKEN_IN_PATH, '$1[token]')
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false
  const proto = Object.getPrototypeOf(value) as unknown
  return proto === Object.prototype || proto === null
}

/** A scrubbed COPY of strings, arrays and plain objects; anything else passes through untouched. */
function scrub<T>(value: T, seen = new WeakSet<object>()): T {
  if (typeof value === 'string') return scrubString(value) as T
  if (Array.isArray(value)) {
    if (seen.has(value)) return value
    seen.add(value)
    return value.map((v: unknown) => scrub(v, seen)) as T
  }
  if (isPlainObject(value)) {
    if (seen.has(value)) return value
    seen.add(value)
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) out[k] = scrub(v, seen)
    return out as T
  }
  return value
}

/*
 * Pages are named by route id (`school-classes`, `invite`), not by URL. The app
 * routes on the hash, so the SDK's default — `location.pathname` — names every
 * page `/`; and the hash itself carries record ids and the credentials above.
 */
function routeName(): string {
  return matchRoute(window.location.hash).id
}

const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? ''

Sentry.init({
  dsn: 'https://43b2f6bea34204fcaa85912076e994c5@o4512248813715456.ingest.us.sentry.io/4512248817778688',

  /* The Playwright and perf builds drive the app with synthetic input; their errors are not users'. */
  enabled: import.meta.env.VITE_E2E_HOOKS !== 'true',
  environment: import.meta.env.MODE,
  /* The commit SHA, set by the deploy workflow. Unset locally. */
  release: import.meta.env.VITE_SENTRY_RELEASE || undefined,

  integrations: [
    Sentry.browserTracingIntegration({
      beforeStartSpan: (options) => ({ ...options, name: routeName() }),
    }),
  ],

  /*
   * ⚠️ 10% IN PRODUCTION, NOT 100%. Every page load and API call is a span, and
   * the product is built for whole school districts at once — 100% would spend
   * the quota on traces in a day. Errors are not sampled; every one is sent.
   */
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  /*
   * Same-origin requests, plus the API. The API's CORS (`enableCors` in
   * backend/src/main.ts, and the socket gateways) sets no `allowedHeaders`, so
   * it echoes back whatever a preflight asks for — `sentry-trace` and `baggage`
   * included. Never add the asset CDN or Firebase here: they would refuse the
   * extra headers at the preflight and the request would fail.
   */
  tracePropagationTargets: [/^\//, ...(API_BASE_URL ? [API_BASE_URL] : [])],

  /*
   * 🔴 A CHILDREN'S PRODUCT: SEND THE ERROR, NOT THE PEOPLE. The SDK's defaults
   * attach IP address, cookies, headers, query strings and HTTP bodies — and an
   * API response here is a class roster or a parent's phone number. All off.
   */
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: false,
    httpBodies: [],
    urlQueryParams: false,
  },

  beforeSend: (event) => scrub(event),
  beforeSendSpan: (span) => scrub(span),
  beforeSendLog: (log) => scrub(log),
  beforeBreadcrumb: (breadcrumb) => {
    /*
     * A console breadcrumb keeps the raw objects that were logged, which can be
     * live app state with names in it. The message (the logged strings) is
     * enough to read the trail; the objects stay in the browser.
     */
    if (breadcrumb.category === 'console' && breadcrumb.data) {
      const { arguments: _args, ...rest } = breadcrumb.data
      return scrub({ ...breadcrumb, data: rest })
    }
    return scrub(breadcrumb)
  },
})
